/* Main document stepper component for creating invoices and quotes
   Updated: Admin-created invoices now also insert into the orders table so they appear
   in Bestellingen. Invoice numbers use shared YYYY-NNNN sequence with orders.
   Updated: Replaced edge function delivery calculation with flat transport contribution.
   Updated: "Versturen" saves the document once and then asks the server to mail exactly that stored
   document via Brevo (one action id; a retry after an error reuses the same document and number, so no
   duplicate documents, orders or numbers). EmailJS removed.
   Updated: download and send both use the server-rendered PDF (identical to the mail attachment) via the
   "Controleren & versturen" dialog. Edits made after an earlier save/download are detected and written to the
   same draft (same number) before previewing; sent documents can no longer change. */

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { calculateBTWBreakdown } from '@/lib/btw-calculator';
import { previewDocument, updateDocumentDraft } from '@/lib/transactional-mail';
import DocumentSendDialog, { downloadPreviewPdf } from './DocumentSendDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Send, Download, Loader as Loader2, Euro, Percent, RefreshCw, Info } from 'lucide-react';
import StepIndicator from './StepIndicator';
import ProductStep from './ProductStep';
import CustomerStep from './CustomerStep';
import ReviewStep from './ReviewStep';
import type { LineItem, CustomerData, DocumentType } from './types';

interface DocumentStepperProps {
  documentType: DocumentType;
  onComplete: () => void;
  onCancel: () => void;
}

const STEPS = ['Producten', 'Klantgegevens', 'Controleren & Versturen'];

const emptyCustomer: CustomerData = {
  name: '',
  email: '',
  phone: '',
  address: '',
  street: '',
  housenumber: '',
  housenumber_addition: '',
  city: '',
  postal_code: '',
  notes: '',
  valid_until: '',
};

interface SavedDocument {
  id: string;
  number: string;
  contentKey: string;
}

export default function DocumentStepper({ documentType, onComplete, onCancel }: DocumentStepperProps) {
  const [step, setStep] = useState(1);
  const [items, setItems] = useState<LineItem[]>([]);
  const [customer, setCustomer] = useState<CustomerData>(emptyCustomer);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryFeeManuallySet, setDeliveryFeeManuallySet] = useState(false);
  const [deliveryCalcLoading, setDeliveryCalcLoading] = useState(false);
  const [deliveryCalcMessage, setDeliveryCalcMessage] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sendDocumentId, setSendDocumentId] = useState<string | null>(null);
  const [documentNumber, setDocumentNumber] = useState('');
  const savedDocRef = useRef<SavedDocument | null>(null);
  const calcDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isInvoice = documentType === 'invoice';
  const subtotal = items.reduce((sum, i) => sum + i.total_price, 0);
  const total = subtotal + deliveryFee - discountAmount;

  const generateDocNumber = async () => {
    if (documentNumber) return documentNumber;
    const rpcName = isInvoice ? 'generate_invoice_number' : 'generate_quote_number';
    const { data, error } = await supabase.rpc(rpcName);
    if (error) throw error;
    setDocumentNumber(data);
    return data as string;
  };

  const [transportSettings, setTransportSettings] = useState<{ transport_contribution_cents: number; free_from_amount_cents: number } | null>(null);

  useEffect(() => {
    supabase
      .from('delivery_settings')
      .select('setting_value')
      .eq('setting_key', 'transport_contribution')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.setting_value) setTransportSettings(data.setting_value);
      });
  }, []);

  const calculateDelivery = useCallback(async (currentItems: LineItem[], currentCustomer: CustomerData) => {
    if (currentItems.length === 0) return;
    if (!currentCustomer.postal_code || !currentCustomer.city) return;
    if (!transportSettings) return;

    const currentSubtotal = currentItems.reduce((sum, i) => sum + i.total_price, 0);
    const freeFrom = transportSettings.free_from_amount_cents / 100;
    const flatFee = transportSettings.transport_contribution_cents / 100;
    const isFree = currentSubtotal >= freeFrom;
    const fee = isFree ? 0 : flatFee;

    if (!deliveryFeeManuallySet) {
      setDeliveryFee(Math.round(fee * 100) / 100);
    }
    setDeliveryCalcMessage(
      isFree
        ? `Gratis bezorging (bestelling boven €${freeFrom.toFixed(2)})`
        : `Vaste transportbijdrage: €${fee.toFixed(2)} (gratis vanaf €${freeFrom.toFixed(2)})`
    );
  }, [deliveryFeeManuallySet, transportSettings]);

  useEffect(() => {
    if (step !== 2) return;
    if (calcDebounceRef.current) clearTimeout(calcDebounceRef.current);
    calcDebounceRef.current = setTimeout(() => {
      calculateDelivery(items, customer);
    }, 300);
    return () => {
      if (calcDebounceRef.current) clearTimeout(calcDebounceRef.current);
    };
  }, [step, customer.postal_code, customer.city, items, calculateDelivery]);

  const validateStep1 = (): boolean => {
    if (items.length === 0) {
      toast.error('Voeg minimaal 1 product toe');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!customer.name.trim()) newErrors.name = 'Naam is verplicht';
    if (!customer.email.trim()) newErrors.email = 'E-mail is verplicht';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) {
      newErrors.email = 'Ongeldig e-mailadres';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = async () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;

    if (step === 2) {
      try {
        await generateDocNumber();
      } catch (error) {
        toast.error('Fout bij het genereren van documentnummer');
        return;
      }
    }

    setStep((s) => Math.min(s + 1, 3));
  };

  const goBack = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 1));
  };

  const buildDraftPayload = () => ({
    customer_name: customer.name,
    customer_email: customer.email,
    customer_phone: customer.phone,
    customer_address: [customer.street, customer.housenumber, customer.housenumber_addition].filter(Boolean).join(' ') || customer.address,
    customer_city: customer.city,
    customer_postal_code: customer.postal_code,
    customer_notes: customer.notes || null,
    items: items.map((i) => ({
      description: i.description,
      quantity: i.quantity,
      unit_price: i.unit_price,
      total_price: i.total_price,
      is_custom: i.is_custom,
      btw_percentage: i.btw_percentage,
      variant_size: i.variant_size || null,
    })),
    subtotal,
    delivery_fee: deliveryFee,
    discount_amount: discountAmount,
    total,
    tax: calculateBTWBreakdown(items.map((i) => ({ totalInclBTW: i.total_price, btwPercentage: i.btw_percentage }))).totalBTWAmount,
    valid_until: customer.valid_until || null,
  });

  const saveDocument = async (docNum: string): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();

    const { data: adminDoc, error } = await supabase.from('admin_documents').insert({
      document_number: docNum,
      document_type: documentType,
      customer_name: customer.name,
      customer_email: customer.email,
      customer_phone: customer.phone,
      customer_address: [customer.street, customer.housenumber, customer.housenumber_addition].filter(Boolean).join(' ') || customer.address,
      customer_city: customer.city,
      customer_postal_code: customer.postal_code,
      customer_notes: customer.notes || null,
      items: items.map((i) => ({
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price,
        total_price: i.total_price,
        is_custom: i.is_custom,
        btw_percentage: i.btw_percentage,
      })),
      subtotal,
      delivery_fee: deliveryFee,
      discount_amount: discountAmount,
      total,
      valid_until: customer.valid_until || null,
      status: 'draft',
      created_by: user?.id || null,
      sent_at: null,
    }).select('id').maybeSingle();

    if (error) throw error;
    if (!adminDoc) throw new Error('Document kon niet worden opgeslagen');

    if (isInvoice) {
      const orderStatus = 'pending';
      const breakdown = calculateBTWBreakdown(
        items.map((i) => ({ totalInclBTW: i.total_price, btwPercentage: i.btw_percentage }))
      );
      const { error: orderError } = await supabase.from('orders').insert({
        order_number: docNum,
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone || '',
        customer_address: [customer.street, customer.housenumber, customer.housenumber_addition].filter(Boolean).join(' ') || customer.address || '',
        customer_city: customer.city || '',
        customer_postal_code: customer.postal_code || '',
        customer_notes: customer.notes || null,
        subtotal,
        tax: breakdown.totalBTWAmount,
        delivery_fee: deliveryFee,
        total,
        status: orderStatus,
        payment_method: 'bank_transfer',
        delivery_type: 'delivery',
        source: 'admin',
        admin_document_id: adminDoc.id,
        coupon_discount: discountAmount > 0 ? discountAmount : 0,
      });

      if (orderError) throw orderError;

      const { data: orderData } = await supabase
        .from('orders')
        .select('id')
        .eq('order_number', docNum)
        .maybeSingle();

      if (orderData) {
        const orderItems = items.map((i) => ({
          order_id: orderData.id,
          product_name: i.description,
          variant_size: i.variant_size || '-',
          quantity: i.quantity,
          unit_price: i.unit_price,
          total_price: i.total_price,
          btw_percentage: i.btw_percentage,
        }));

        await supabase.from('order_items').insert(orderItems);
      }
    }
    return adminDoc.id;
  };

  const ensureSynced = async (): Promise<SavedDocument> => {
    const payload = buildDraftPayload();
    const contentKey = JSON.stringify(payload);
    const current = savedDocRef.current;
    if (current && current.contentKey === contentKey) return current;
    if (current) {
      await updateDocumentDraft(current.id, payload);
      const updated = { ...current, contentKey };
      savedDocRef.current = updated;
      toast.info(`Wijzigingen opgeslagen in ${current.number}`, { description: 'Het voorbeeld toont de nieuwe versie.' });
      return updated;
    }
    const docNum = documentNumber || (await generateDocNumber());
    const saved = { id: await saveDocument(docNum), number: docNum, contentKey };
    savedDocRef.current = saved;
    return saved;
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const saved = await ensureSynced();
      setSendDocumentId(saved.id);
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Document opslaan mislukt', { description: error instanceof Error ? error.message : undefined, duration: 10000 });
    } finally {
      setSending(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const saved = await ensureSynced();
      const preview = await previewDocument(saved.id);
      downloadPreviewPdf(preview);
      toast.success(`${isInvoice ? 'Factuur' : 'Offerte'} gedownload als ${preview.attachment.name}`);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Er is iets misgegaan bij het downloaden', { description: error instanceof Error ? error.message : undefined });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <StepIndicator currentStep={step} steps={STEPS} />

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
        >
          {step === 1 && (
            <div className="space-y-6">
              <ProductStep items={items} onItemsChange={setItems} />

              {items.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                          <Euro className="w-3 h-3" /> Korting (bedrag)
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={discountAmount || ''}
                          onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                          <Percent className="w-3 h-3" /> BTW %
                        </Label>
                        <p className="text-xs text-gray-400 pt-2">Bezorgkosten worden berekend op basis van klantadres in stap 2.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <CustomerStep
                customer={customer}
                onChange={(c) => {
                  setCustomer(c);
                  setDeliveryFeeManuallySet(false);
                }}
                documentType={documentType}
                errors={errors}
              />

              {items.filter((i) => !i.is_custom && i.variant_size).length > 0 && (
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                        <Euro className="w-3.5 h-3.5" /> Bezorgkosten
                      </Label>
                      {deliveryCalcLoading && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <RefreshCw className="w-3 h-3 animate-spin" /> Berekenen...
                        </span>
                      )}
                    </div>

                    {deliveryCalcMessage && (
                      <p className={`text-xs flex items-start gap-1.5 ${deliveryCalcMessage.startsWith('Berekend') ? 'text-[#1e2b22]' : 'text-[#c8a94a]'}`}>
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {deliveryCalcMessage}
                      </p>
                    )}

                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={deliveryFee || ''}
                        onChange={(e) => {
                          setDeliveryFee(parseFloat(e.target.value) || 0);
                          setDeliveryFeeManuallySet(true);
                        }}
                        placeholder="0.00"
                        className="text-sm max-w-[160px]"
                      />
                      {deliveryFeeManuallySet && customer.postal_code && customer.city && (
                        <button
                          type="button"
                          onClick={() => {
                            setDeliveryFeeManuallySet(false);
                            calculateDelivery(items, customer);
                          }}
                          className="text-xs text-jv-primary hover:underline flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" /> Herbereken
                        </button>
                      )}
                    </div>

                    {!customer.postal_code || !customer.city ? (
                      <p className="text-xs text-gray-400">Vul postcode en plaats in om bezorgkosten automatisch te berekenen.</p>
                    ) : null}
                  </CardContent>
                </Card>
              )}

              {items.filter((i) => !i.is_custom && i.variant_size).length === 0 && items.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                        <Euro className="w-3 h-3" /> Bezorgkosten (handmatig)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={deliveryFee || ''}
                        onChange={(e) => setDeliveryFee(parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                        className="text-sm"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {step === 3 && (
            <ReviewStep
              items={items}
              customer={customer}
              documentType={documentType}
              documentNumber={documentNumber}
              deliveryFee={deliveryFee}
              discountAmount={discountAmount}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-8 pt-6 border-t">
        <div>
          {step === 1 ? (
            <Button variant="ghost" onClick={onCancel}>
              Annuleren
            </Button>
          ) : (
            <Button variant="outline" onClick={goBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Vorige
            </Button>
          )}
        </div>

        <div className="flex gap-3">
          {step === 3 && (
            <>
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={downloading || sending}
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || downloading}
                className="bg-jv-primary hover:bg-jv-primary/90"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Controleer & verstuur {isInvoice ? 'factuur' : 'offerte'}
              </Button>
            </>
          )}

          {step < 3 && (
            <Button onClick={goNext} className="bg-jv-primary hover:bg-jv-primary/90">
              Volgende
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      <DocumentSendDialog
        documentId={sendDocumentId}
        onClose={() => setSendDocumentId(null)}
        onSent={onComplete}
      />
    </div>
  );
}
